import { RootScopeEvents } from '../../../../../common/services/root-scope-types';
import { AudioContextClass } from '../../../../../global/window';
import * as call from '../../../../../modules/call';
import { playOnLoadedData } from '../../../../../modules/call/helpers';
import { AngularServices } from '../../../../../modules/core';
import { BASE64_PREFIX } from '../../../../../modules/core/constants';
import { setCaptions, getCaptions, updateCaptionsArray, getIsCaptionsOpen, } from '../../../../../modules/captions';
import { TokboxMicrophoneRenderer } from '../../tokbox-microphone-renderer';
import { TokboxVolumeListener } from '../../tokbox-volume-listener';
import { getPlaceholderFromConnectionState } from '../helpers/get-placeholder-from-connection-state.helper';
import StatsMapperHelper from '../helpers/stats-mapper.helper';
import TokboxSubscriberListener from '../tokbox-subscriber-listener';
export default class TokboxMediaStream {
    constructor(personConnection, stream) {
        this.personConnection = personConnection;
        this.stream = stream;
        this.type = 'camera';
        this.isWaiting = true;
        this.QUALITY_CHANGE_DELAY = 2500;
        this.videoDisabledReasons = {
            codecNotSupported: false,
            quality: false,
            publishVideo: false,
            dimensions: false,
            subscribeToVideo: false,
        };
        this.videoDisabledReasons.publishVideo = !this.stream.hasVideo;
        this.$timeout = this.personConnection.$injector.get(AngularServices.Timeout);
        this.$rootScope = this.personConnection.$injector.get(AngularServices.RootScope);
        const $log = this.personConnection.$injector.get('$log');
        this.debug = $log.debug.bind($log, 'TokboxMediaStream:');
        this.Browser = this.personConnection.$injector.get('Browser');
        this.downgradeCallStateService = this.personConnection.$injector.get(AngularServices.DowngradeCallStateService);
        this.$rootScope.$emit(RootScopeEvents.STREAM.CREATED, this);
        this.createSubscriber();
        this.setUpVolumeListener();
        this.setUpVideoElement();
        /**
         * https://tokbox.com/developer/sdks/js/reference/Subscriber.html#events
         */
        this.subscriber?.on('videoDisabled', (e) => {
            const timeDiff = Date.now() - this.lastQualityChangeTimestamp; // watching if there was quality change in last moments
            this.debug(`videoDisabled: timeDiff: ${timeDiff}`);
            this.qualityChangeTimeout = this.$timeout(() => {
                this.videoDisabledReasons[e.reason] = true;
                this.debug(`videoDisabled: qualityChangeTimeout`);
            }, timeDiff < this.QUALITY_CHANGE_DELAY ? this.QUALITY_CHANGE_DELAY : 0);
        });
        this.subscriber?.on('videoEnabled', (e) => {
            if (this.qualityChangeTimeout) {
                this.$timeout.cancel(this.qualityChangeTimeout);
            }
            if (e.reason === 'publishVideo') {
                this.downgradeCallStateService.turnOnVideo();
            }
            void this.$timeout(() => {
                this.videoDisabledReasons[e.reason] = false;
                this.videoDisabledReasons.dimensions = false;
            });
        });
        this.subscriber?.on('destroyed', () => {
            this.cleanUpFixIos15LowAudio();
            this.$rootScope.$emit(RootScopeEvents.SUBSCRIBER.DESTROYED, this.subscriber);
            if (Boolean(this.subscriber.stream)) {
                this.destroy();
            }
        });
    }
    get id() {
        return this.stream.id;
    }
    get volumeLevel() {
        return this.stream.volumeLevel ?? 0;
    }
    get hasAudio() {
        return this.stream.hasAudio;
    }
    get hasVideo() {
        return Object.values(this.videoDisabledReasons).every((isDisabled) => !isDisabled);
    }
    get isVideoElMuted() {
        const videoEl = this.getVideoElement();
        if (!videoEl) {
            return false;
        }
        return call.helpers.isSoundPlaying(videoEl);
    }
    get placeholder() {
        if (!this.personConnection.state) {
            return '';
        }
        const placeholder = getPlaceholderFromConnectionState(this.personConnection.state);
        if (placeholder) {
            return placeholder;
        }
        if (!this.hasVideo) {
            if (this.videoDisabledReasons.publishVideo) {
                return 'video-chat.camera.turned-off';
            }
            else if (this.videoDisabledReasons.codecNotSupported) {
                return 'video-chat.camera.disabled-by-unsupported-codec';
            }
            else if (this.videoDisabledReasons.quality) {
                return 'video-chat.camera.disabled-by-quality';
            }
            else {
                // dimensions or subscribeToVideo
                return 'video-chat.camera.turned-off';
            }
        }
        return '';
    }
    onConstraintsApplied() {
        this.lastQualityChangeTimestamp = Date.now();
    }
    toggleVideoSubscription(isSubscribed) {
        if (Boolean(this.subscriber?.stream)) {
            this.subscriber.subscribeToVideo(isSubscribed);
        }
    }
    getVideoElement() {
        return this.subscriber?.videoElement();
    }
    getImgData() {
        if (!this.subscriber?.isSubscribing()) {
            return '';
        }
        const imageData = this.subscriber.getImgData();
        if (!imageData) {
            return '';
        }
        return BASE64_PREFIX + imageData;
    }
    async getStats() {
        if (!this.subscriber?.isSubscribing()) {
            return { audio: null, video: null };
        }
        if (!this.hasAudio && !this.hasVideo) {
            return { audio: null, video: null };
        }
        return new Promise((resolve) => {
            this.subscriber.getStats((_, stats) => {
                if (!this.hasAudio) {
                    stats.audio = null;
                }
                if (!this.hasVideo) {
                    stats.video = null;
                }
                const mappedStats = StatsMapperHelper.mapStatsItem(stats);
                resolve(mappedStats);
            });
        });
    }
    destroy() {
        this.personConnection.session.unsubscribe(this.subscriber);
    }
    unmuteVideoEl() {
        const videoEl = this.getVideoElement();
        call.helpers.resumeSoundPlayback(videoEl);
        this.$rootScope.$emit(RootScopeEvents.STREAM.UNMUTE_VIDEO_EL);
    }
    setUpVideoElement() {
        this.videoElementPromise = new Promise((resolve) => {
            this.subscriber?.on('videoElementCreated', (event) => {
                this.videoElement = this.subscriber.videoElement();
                if (this.Browser.isIosWithLowAudio()) {
                    this.fixIos15LowAudio(this.videoElement);
                }
                if (this.Browser.isSafari(15)) {
                    this.fixSafari15Freeze(this.videoElement);
                }
                void this.$timeout(() => {
                    if (!Boolean(this.stream.videoDimensions.width) &&
                        !Boolean(this.stream.videoDimensions.height)) {
                        this.videoDisabledReasons.dimensions = true;
                    }
                    this.isWaiting = false;
                    this.normalizeSafariTracks(event.element.srcObject);
                    this.$rootScope.$emit(RootScopeEvents.SUBSCRIBER.PARTICIPANT_VIDEO_APPEARED, {
                        uuid: this.personConnection.person.uuid,
                        stream: this,
                    });
                    resolve(this.videoElement);
                });
            });
        });
    }
    /**
     *  @issue        https://doxyme.atlassian.net/browse/CS-3835 and https://doxyme.atlassian.net/browse/CS-3936
     *  @description  Fixes subscriber video freezing after call restart
     *  @todo         Monitor subsequent browser releases and identify the range of versions that have this bug
     *                Affected versions: Safari 15.0 (desktop and mobile)
     * */
    fixSafari15Freeze(el) {
        const PLAY_DELAY_MS = 200;
        playOnLoadedData(el, PLAY_DELAY_MS);
    }
    /**
     *  @issue        https://doxyme.atlassian.net/browse/CS-3540
     *                webkit: https://bugs.webkit.org/show_bug.cgi?id=230902
     *  @description  Fixes low audio issue on iOS 15.1+
     *  @todo         Check the bug after release of iOS 15.3
     * */
    fixIos15LowAudio(el) {
        const audioTrack = el.srcObject.getAudioTracks()[0];
        if (!Boolean(audioTrack)) {
            return;
        }
        this.audioContext = new AudioContextClass();
        const audioNode = this.audioContext.createMediaStreamSource(new MediaStream([audioTrack]));
        const gainNode = this.audioContext.createGain();
        audioNode.connect(gainNode);
        gainNode.connect(this.audioContext.destination);
        gainNode.gain.value = 20;
    }
    /**
     *  @issue        We need to close audioContext created in fixIos15LowAudio
     *  @description  https://developer.mozilla.org/en-US/docs/Web/API/AudioContext/close
     *  @todo         Remove when fixIos15LowAudio is removed
     * */
    cleanUpFixIos15LowAudio() {
        void this.audioContext?.close();
    }
    createSubscriber() {
        const props = {
            insertDefaultUI: false,
        };
        this.subscriber = this.personConnection.session.subscribe(this.stream, null, props, (err) => {
            if (Boolean(err)) {
                this.$rootScope.$emit(RootScopeEvents.STREAM.SUBSCRIPTION_ERROR, {
                    stream: this,
                    event: err,
                });
            }
        });
    }
    setUpVolumeListener() {
        this.subscriber?.on('loaded', () => {
            this.$rootScope.$emit(RootScopeEvents.SUBSCRIBER.CREATED, this.subscriber);
            const subscriberListener = new TokboxSubscriberListener(this.personConnection.$injector, this.subscriber);
            subscriberListener.on('no-sound', () => {
                this.$rootScope.$emit('media-stream:no-sound', this);
            });
            const microphoneRenderer = new TokboxMicrophoneRenderer(this.stream.id, this.$rootScope);
            const volumeListener = new TokboxVolumeListener(this.subscriber);
            volumeListener.events.on('update', (level) => {
                this.stream.volumeLevel = level;
                microphoneRenderer.renderMicrophone(level);
            });
            // Subscribe to captions if available
            void this.setupCaptionSubscription();
        });
    }
    async setupCaptionSubscription() {
        if (!this.subscriber) {
            return;
        }
        try {
            await this.subscriber.subscribeToCaptions(true);
            this.subscriber.on('captionReceived', (captionEvent) => {
                if (captionEvent.caption === undefined) {
                    return;
                }
                const username = this.personConnection.userName;
                const currentState = this.personConnection.$ngRedux.getState();
                const isCaptionsOpened = getIsCaptionsOpen(currentState);
                /**
                 * No need to update current state if captions are not enabled.
                 */
                if (!isCaptionsOpened) {
                    return;
                }
                const currentCaptions = getCaptions(currentState);
                const newCaption = {
                    name: username,
                    streamId: captionEvent.streamId,
                    caption: captionEvent.caption,
                    isFinal: captionEvent.isFinal ?? false,
                    showName: true,
                };
                const updatedCaptions = updateCaptionsArray(currentCaptions, newCaption);
                this.personConnection.$ngRedux.dispatch(setCaptions(updatedCaptions));
            });
        }
        catch (error) {
            this.$rootScope.$emit(RootScopeEvents.CLOSED_CAPTIONS.FAIL_TO_SUBSCRIBE, error);
        }
    }
    normalizeSafariTracks(stream) {
        if (!this.Browser.isSafari()) {
            return;
        }
        if (!stream) {
            return;
        }
        const videoTrack = stream.getVideoTracks()[0];
        if (!Boolean(videoTrack)) {
            return;
        }
        if (videoTrack.enabled) {
            return;
        }
        setTimeout(() => {
            videoTrack.enabled = true;
            setTimeout(() => (videoTrack.enabled = false), 500);
        }, 100);
    }
}
